import React, { useEffect, useMemo } from "react";
import { Box, Paper, Typography } from "@mui/material";
import SummarizeIcon from "@mui/icons-material/Summarize";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import Markdown from "react-markdown";
import rehypeRaw from "rehype-raw";

const markdownStyles = {
  h2: {
    fontFamily: "'Inter', 'Inter Fallback', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
    fontSize: "15px",
    color: "#1e3a8a",
    fontWeight: "600",
    marginTop: "1.5em",
    marginBottom: "1em",
  },
  h3: {
    fontSize: "14px",
    fontFamily: "'Inter', 'Inter Fallback', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
    fontWeight: "bold",
    marginBottom: "4px",
  },
  h4: {
    fontSize: "13px",
    fontFamily: "'Inter', 'Inter Fallback', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
    fontWeight: "bold",
    marginTop: "6px",
    marginBottom: "6px",
  },
  p: {
    fontFamily: "'Inter', 'Inter Fallback', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
    fontSize: "13px",
    color: "#334155",
    marginBottom: "1.25em",
    lineHeight: "1.5",
  },
  ol: {
    fontFamily: "'Inter', 'Inter Fallback', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
    fontSize: "14px",
    marginBottom: "6px",
    paddingLeft: "20px",
    listStyleType: "decimal",
  },
  ul: {
    fontFamily: "'Inter', 'Inter Fallback', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
    fontSize: "14px",
    marginBottom: "6px",
    paddingLeft: "20px",
    listStyleType: "disc",
  },
  li: {
    fontFamily: "'Inter', 'Inter Fallback', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
    fontSize: "13px",
    color: "#334155",
    lineHeight: "1.5",
  },
  strong: {
    fontWeight: 600,
  },
  mark: {
    fontFamily: "'Inter', 'Inter Fallback', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
    backgroundColor: "#ffeb3b",
    padding: "0 2px",
    fontSize: "inherit",
    fontWeight: "inherit",
  },
};

const CaseDetailSummary = ({
  searchTerm,
  currentMatch,
  setCurrentMatch,
  setTotalMatches,
  summary,
}) => {
  const escapeRegExp = (string) => {
    return string.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  };

  const highlightedContent = useMemo(() => {
    if (!searchTerm) {
      setTotalMatches(0);
      return summary;
    }

    const keywordPattern = new RegExp(`(${escapeRegExp(searchTerm)})`, "gi");
    let matchIndex = 0;

    const contentWithHighlights = summary?.split(keywordPattern);

    const highlighted = contentWithHighlights
      ?.map((part) => {
        // Check for exact match to avoid regex.test issues
        if (part.toLowerCase() === searchTerm.toLowerCase()) {
          const isCurrent = matchIndex === currentMatch;
          const style = isCurrent ? 'style="border: 1px solid #303f9f;"' : "";
          const marked = `<mark class="highlight" data-index="${matchIndex}" ${style}>${part}</mark>`;
          matchIndex++;
          return marked;
        }
        return part;
      })
      .join("");

    setTotalMatches(matchIndex);
    return highlighted;
  }, [summary, searchTerm, currentMatch]);

  useEffect(() => {
    const match = document.querySelector(
      `.highlight[data-index="${currentMatch}"]`
    );
    if (match) {
      match.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [currentMatch]);

  return (
    <Box sx={{ p: { xs: 1, lg: 2 } }}>
      <Paper
        elevation={0}
        sx={{
          p: { xs: 1.5, lg: 2.5 },
          bgcolor: "#FFFFFF",
          border: "1px solid #E2E8F0",
          borderRadius: "12px",
          transition: "all 0.2s ease",
          "&:hover": {
            borderColor: "#16A34A",
            boxShadow: "0 4px 12px rgba(22, 163, 74, 0.08)",
          },
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: { xs: "flex-start", lg: "center" },
            gap: 1.5,
            pb: 2,
            borderBottom: "1px solid #E2E8F0",
          }}
        >
          <Box
            sx={{
              width: 40,
              height: 40,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: "#F0FDF4",
              borderRadius: "10px",
              color: "#16A34A",
            }}
          >
            <SummarizeIcon sx={{ fontSize: "1.25rem" }} />
          </Box>
          <Box>
            <Typography
              sx={{
                fontSize: { xs: "16px", lg: "18px" },
                fontWeight: 600,
                color: "#1E293B",
                mb: 0.5,
              }}
            >
              Detailed Case Summary
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: "#334155",
                fontSize: "0.875rem",
              }}
            >
              Comprehensive overview of the case proceedings and judgment
            </Typography>
          </Box>
        </Box>

        {/* Summary Content */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          {!summary ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                boxSizing: "border-box",
                minHeight: "150px",
                bgcolor: "#fff",
                p: "16px",
                m: 1,
                borderRadius: "8px",
                border: "1px solid #ddd",
                boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
                overflowY: "auto",
                "::-webkit-scrollbar": {
                  width: "5px",
                },
                "::-webkit-scrollbar-track": {
                  background: "#fff",
                },
                "::-webkit-scrollbar-thumb": {
                  background: "#bbb",
                  borderRadius: "6px",
                },
              }}
            >
              <ErrorOutlineIcon
                sx={{
                  fontSize: { xs: "20px", lg: "30px" },
                  mb: 1,
                  color: "#ff9800",
                }}
              />
              <Typography
                sx={{
                  textAlign: "center",
                  fontSize: { xs: "12px", lg: "14px" },
                  color: "#555",
                  fontWeight: "bold",
                }}
              >
                Case summary not found
              </Typography>
            </Box>
          ) : (
            <Typography sx={{ textAlign: "justify" }}>
              <Markdown
                rehypePlugins={[rehypeRaw]}
                components={{
                  h1: ({ node, ...props }) => (
                    <Typography sx={{ display: "none" }} {...props} />
                  ),
                  h2: ({ node, ...props }) => (
                    <Typography sx={markdownStyles.h2} {...props} />
                  ),
                  h3: ({ node, ...props }) => (
                    <Typography sx={markdownStyles.h3} {...props} />
                  ),
                  h4: ({ node, ...props }) => (
                    <Typography sx={markdownStyles.h4} {...props} />
                  ),
                  p: ({ node, ...props }) => (
                    <Typography sx={markdownStyles.p} {...props} />
                  ),
                  ol: ({ node, ...props }) => (
                    <Box component="ol" sx={markdownStyles.ol} {...props} />
                  ),
                  ul: ({ node, ...props }) => (
                    <Box component="ul" sx={markdownStyles.ul} {...props} />
                  ),
                  li: ({ node, ...props }) => (
                    <Typography
                      component="li"
                      sx={markdownStyles.li}
                      {...props}
                    />
                  ),
                  mark: ({ node, ...props }) => (
                    <Typography
                      component="mark"
                      sx={markdownStyles.mark}
                      {...props}
                    />
                  ),
                }}
              >
                {highlightedContent}
              </Markdown>
            </Typography>
          )}
        </Box>
      </Paper>
    </Box>
  );
};

export default CaseDetailSummary;
